import { useEffect, useState, createContext, useContext, useRef, useCallback, useMemo } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, useNavigate, useParams } from "react-router-dom";
import { PlayerProvider, usePlayer } from "@/components/contexts/PlayerContext";
import { CountryProvider, useCountry } from "@/components/contexts/CountryContext";
import { UserProvider, useUser } from "@/components/contexts/UserContext";
import { AudioProvider, useAudio } from "@/components/contexts/AudioContext";
import { SearchPage } from "@/pages/SearchPage";
import { LibraryPage } from "@/pages/LibraryPage";
import { VideoPage } from "@/pages/VideoPage";
import { RisingPage } from "@/pages/RisingPage";
import { AccountPage } from "@/pages/AccountPage";
import { InstallPrompt } from "@/components/InstallPrompt";
import { FollowButton } from "@/components/FollowButton";
import { LeaderboardPage } from "@/pages/LeaderboardPage";
import { ComparePage } from "@/pages/ComparePage";
import { ArchivePage } from "@/pages/ArchivePage";
import { ChannelPage } from "@/pages/ChannelPage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { PrivacyPolicyPage } from "@/pages/legal/PrivacyPolicyPage";
import { TermsPage } from "@/pages/legal/TermsPage";
import { DeleteAccountPage } from "@/pages/legal/DeleteAccountPage";
import { AdvancedFilters } from "@/components/AdvancedFilters";
import { DigestSignup } from "@/components/DigestSignup";
import { ShareMenu } from "@/components/ShareMenu";
import { UpdatePrompt } from "@/components/UpdatePrompt";
import { RankMovement, VelocityBadge } from "@/components/RankMovement";
import { API, IS_NATIVE, PLAY_STORE_URL, ytEmbedUrl, YT_MESSAGE_ORIGIN } from "@/lib/config";
import { LEGAL } from "@/lib/legal";
import { useFilterState } from "@/lib/useFilterState";
import { recordWatch, isSaved as isVideoSaved, toggleSaved as toggleVideoSaved } from "@/lib/storage";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, Play, Download, Zap, Search, Globe, ChevronDown, X, Menu, TrendingUp, Film, Music, Gamepad2, Newspaper, Sparkles, Youtube, Instagram, Filter, Loader2, Volume2, VolumeX, Volume1, ThumbsUp, Eye, Users, Clock, MessageCircle, BarChart3, Timer, Info, Maximize, Lock, LogOut, DollarSign, Activity, MapPin, Video, UserPlus, Shield, Trash2, UserCheck, UserX, Trophy, GitCompare, CalendarDays, BookMarked, Bookmark, UserCircle, ArrowUpRight } from "lucide-react";
import { Toaster, toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from "recharts";

// Imported rather than recomputed: the native build resolves a different backend
// host, and a second inline copy of this would silently keep pointing at the web
// one from inside the packaged app.

// Auth Context for Admin
const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem('adminToken'));
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          const res = await axios.get(`${API}/admin/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setAdmin(res.data);
        } catch (e) {
          localStorage.removeItem('adminToken');
          setToken(null);
          setAdmin(null);
        }
      }
      setLoading(false);
    };
    verifyToken();
  }, [token]);

  const login = async (username, password) => {
    const res = await axios.post(`${API}/admin/login`, { username, password });
    const { access_token } = res.data;
    localStorage.setItem('adminToken', access_token);
    setToken(access_token);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    setToken(null);
    setAdmin(null);
  };

  return (
    <AuthContext.Provider value={{ token, admin, login, logout, loading, isAuthenticated: !!token }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

// Utility function to convert timestamp to "time ago" format
const timeAgo = (dateString) => {
  if (!dateString) return "";

  const now = new Date();
  const date = new Date(dateString);
  const seconds = Math.floor((now - date) / 1000);

  const intervals = [
    { label: 'year', seconds: 31536000 },
    { label: 'month', seconds: 2592000 },
    { label: 'week', seconds: 604800 },
    { label: 'day', seconds: 86400 },
    { label: 'hour', seconds: 3600 },
    { label: 'minute', seconds: 60 },
  ];

  for (const interval of intervals) {
    const count = Math.floor(seconds / interval.seconds);
    if (count >= 1) {
      return `${count} ${interval.label}${count > 1 ? 's' : ''} ago`;
    }
  }

  return 'Just now';
};

// Country state lives in components/contexts/CountryContext.jsx so that pages and
// components outside this file share the same provider instance. Re-exported here
// because the rest of this file already imports it by this name.
export { useCountry };

// Playback state moved to its own module so PlayerContext and the video page
// can use it without importing App.js and forming a cycle.
export { useAudio };

// Custom hook for infinite scroll
const useInfiniteScroll = (callback, hasMore) => {
  const observer = useRef();

  const lastElementRef = useCallback(node => {
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        callback();
      }
    }, { threshold: 0.1, rootMargin: '100px' });
    if (node) observer.current.observe(node);
  }, [callback, hasMore]);

  return lastElementRef;
};

// Fixed autoplay hook - with delay for mobile scroll
const useSingleVideoAutoplay = (videoId, cardRef, isHovered) => {
  const { activeVideoId, setActiveVideoId, feedAutoplayAllowed } = useAudio();
  const [isMobile, setIsMobile] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldPlay, setShouldPlay] = useState(false);
  const [isPageVisible, setIsPageVisible] = useState(true);
  const delayTimerRef = useRef(null);

  // Track page/tab visibility - pause when user leaves
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(!document.hidden);
    };

    const handlePageHide = () => {
      setIsPageVisible(false);
    };

    const handlePageShow = () => {
      setIsPageVisible(true);
    };

    // Listen for tab visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    // Listen for page navigation (back/forward)
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);
    // Listen for window blur (user clicks outside browser)
    window.addEventListener('blur', () => setIsPageVisible(false));
    window.addEventListener('focus', () => setIsPageVisible(true));

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('blur', () => setIsPageVisible(false));
      window.removeEventListener('focus', () => setIsPageVisible(true));
    };
  }, []);

  useEffect(() => {
    const checkMobile = () => window.innerWidth < 768;
    setIsMobile(checkMobile());
    const handleResize = () => setIsMobile(checkMobile());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Use IntersectionObserver for visibility detection
  useEffect(() => {
    if (!cardRef.current) return;

    const checkCenterVisibility = (entry) => {
      if (!entry.isIntersecting) return false;

      const rect = entry.boundingClientRect;
      const viewportHeight = window.innerHeight;
      const elementTop = rect.top;
      const elementBottom = rect.bottom;
      const elementHeight = rect.height;

      const visibleTop = Math.max(0, elementTop);
      const visibleBottom = Math.min(viewportHeight, elementBottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      const visibleRatio = visibleHeight / elementHeight;

      const elementCenter = elementTop + elementHeight / 2;
      const viewportCenter = viewportHeight / 2;
      const distanceFromCenter = Math.abs(elementCenter - viewportCenter);
      const isNearCenter = distanceFromCenter < viewportHeight * 0.35;

      return visibleRatio > 0.4 && isNearCenter;
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (isMobile) {
            setIsVisible(checkCenterVisibility(entry));
          } else {
            setIsVisible(entry.isIntersecting && entry.intersectionRatio > 0.2);
          }
        });
      },
      {
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
        rootMargin: '0px'
      }
    );

    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [isMobile, cardRef]);

  // Handle delayed autoplay for mobile
  useEffect(() => {
    if (isMobile) {
      // Clear any existing timer
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
        delayTimerRef.current = null;
      }

      if (isVisible) {
        // Start a 1.5 second delay before playing
        delayTimerRef.current = setTimeout(() => {
          setShouldPlay(true);
        }, 1500);
      } else {
        setShouldPlay(false);
      }

      return () => {
        if (delayTimerRef.current) {
          clearTimeout(delayTimerRef.current);
        }
      };
    } else {
      // Desktop: immediate on hover
      setShouldPlay(isHovered);
    }
  }, [isMobile, isVisible, isHovered]);

  // Set this video as active when conditions are met
  useEffect(() => {
    if (shouldPlay && isPageVisible && feedAutoplayAllowed) {
      setActiveVideoId(videoId);
    }
  }, [shouldPlay, isPageVisible, feedAutoplayAllowed, videoId, setActiveVideoId]);

  // Return true only if this is THE active video, the page is visible, and no
  // full-screen player has claimed playback.
  return activeVideoId === videoId && shouldPlay && isPageVisible && feedAutoplayAllowed;
};

// Platform Filter Component
const PlatformFilter = ({ activePlatform, onPlatformChange }) => {
  // Instagram was listed here but never implemented on the backend - selecting it
  // silently returned YouTube results. Removed until there is something behind it.
  const platforms = [
    { id: "all", label: "All", icon: Filter },
    { id: "youtube", label: "YouTube", icon: Youtube },
    { id: "shorts", label: "Shorts", icon: Zap },
  ];

  return (
    <div className="flex items-center gap-2 mb-3 sm:mb-6 overflow-x-auto no-scrollbar pb-1 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="platform-filter">
      <span className="text-xs sm:text-sm text-white/50 flex-shrink-0">Platform</span>
      {platforms.map((platform) => (
        <button
          key={platform.id}
          onClick={() => onPlatformChange(platform.id)}
          className={`flex items-center gap-1.5 px-3 h-11 sm:h-auto sm:py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 ${activePlatform === platform.id
            ? "bg-primary text-black"
            : "bg-white/5 text-white/70 hover:bg-white/10 hover:text-white border border-white/10"
            }`}
          data-testid={`platform-${platform.id}`}
        >
          <platform.icon className="w-4 h-4" />
          <span>{platform.label}</span>
        </button>
      ))}
    </div>
  );
};

// Global Volume Control Component
const GlobalVolumeControl = () => {
  const { isMuted, toggleMute, volume, setVolume } = useAudio();
  const [showSlider, setShowSlider] = useState(false);

  const VolumeIcon = isMuted || volume === 0 ? VolumeX : volume < 50 ? Volume1 : Volume2;

  // Handle click with proper event handling to prevent navigation
  const handleVolumeClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleMute();
  };

  const handleContainerClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!showSlider) setShowSlider(true);
  };

  const handleSliderChange = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setVolume(parseInt(e.target.value));
  };

  return (
    <div
      className="tz-safe-bottom fixed bottom-24 right-4 sm:bottom-20 sm:right-6 z-[100] flex flex-col-reverse items-center gap-2"
      onMouseEnter={() => setShowSlider(true)}
      onMouseLeave={() => setShowSlider(false)}
      onClick={handleContainerClick}
    >
      <button
        onClick={handleVolumeClick}
        className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full glass flex items-center justify-center hover:bg-white/20 transition-all shadow-lg ${!isMuted ? 'ring-2 ring-primary' : ''}`}
        data-testid="global-volume-btn"
        title={isMuted ? "Unmute" : "Mute"}
      >
        <VolumeIcon className={`w-5 h-5 sm:w-6 sm:h-6 ${isMuted ? 'text-white/50' : 'text-primary'}`} />
      </button>

      <AnimatePresence>
        {showSlider && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 120 }}
            exit={{ opacity: 0, height: 0 }}
            className="w-12 sm:w-14 glass rounded-full flex flex-col items-center justify-center py-4"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="range"
              min="0"
              max="100"
              value={isMuted ? 0 : volume}
              onChange={handleSliderChange}
              onClick={(e) => e.stopPropagation()}
              className="volume-slider-vertical"
              data-testid="global-volume-slider"
            />
            <span className="text-xs text-white/60 mt-2">{isMuted ? 0 : volume}%</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Platform Icon Component
const PlatformIcon = ({ platform, isPlaying }) => {
  const icons = {
    youtube: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
    shorts: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M10 14.65v-5.3L15 12l-5 2.65zm7.77-4.33c-.77-.32-1.2-.5-1.2-.5L18 9.06c1.84-.96 2.53-3.23 1.56-5.06s-3.24-2.53-5.07-1.56L6 6.94c-1.29.68-2.07 2.04-2 3.49.07 1.42.93 2.67 2.22 3.25.03.01 1.2.5 1.2.5L6 14.93c-1.83.97-2.53 3.24-1.56 5.07.97 1.83 3.24 2.53 5.07 1.56l8.5-4.5c1.29-.68 2.06-2.04 1.99-3.49-.07-1.42-.94-2.68-2.23-3.25z" />
      </svg>
    ),
    instagram: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
      </svg>
    ),
    tiktok: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
      </svg>
    ),
  };

  const colors = {
    youtube: "bg-red-600",
    shorts: "bg-red-500",
    instagram: "bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400",
    tiktok: "bg-black",
  };

  return (
    <div className={`w-8 h-8 rounded-lg ${colors[platform] || colors.youtube} flex items-center justify-center text-white shadow-lg ${isPlaying ? 'icon-pulse ring-2 ring-orange-500' : ''}`}>
      {icons[platform] || icons.youtube}
    </div>
  );
};

// Navbar Component
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCountrySelect, setShowCountrySelect] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const { country, setCountry, countries } = useCountry();
  const { isSignedIn } = useUser();

  const navLinks = [
    { path: "/", label: "Trending", icon: Flame },
    { path: "/rising", label: "Rising", icon: TrendingUp },
    { path: "/shorts", label: "Shorts", icon: Zap },
    { path: "/categories", label: "Categories", icon: Film },
    { path: "/leaderboard", label: "Top Clicks", icon: Trophy },
    { path: "/compare", label: "Compare", icon: GitCompare },
    { path: "/archive", label: "Archive", icon: CalendarDays },
    { path: "/library", label: "Library", icon: BookMarked },
  ];

  const filteredCountries = countries.filter(c =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    c.code.toLowerCase().includes(countrySearch.toLowerCase())
  );

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="main-navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* min-w-0 + truncate is what makes the bar hold at any width: the
                wordmark gives up space instead of pushing the controls off the
                right edge. Sizing alone was tuned to 390px and still overflowed
                at 320px. */}
            <NavLink to="/" className="flex items-center gap-2 min-w-0 shrink" data-testid="logo-link">
              <div className="w-9 h-9 sm:w-10 sm:h-10 shrink-0 rounded-xl bg-gradient-to-br from-orange-500 to-yellow-500 flex items-center justify-center">
                <Flame className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
              </div>
              <span className="font-heading text-lg sm:text-xl font-black tracking-tighter text-white truncate">
                Trending<span className="text-orange-500">Z</span>
              </span>
            </NavLink>

            {/* Collapses to the menu button below xl: eight links at md widths
                pushed the country selector off the right edge of the bar. */}
            <div className="hidden xl:flex items-center gap-1">
              {navLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-all ${isActive
                      ? "bg-gradient-to-r from-orange-500 to-yellow-500 text-black"
                      : "text-white/70 hover:text-white hover:bg-white/10"
                    }`
                  }
                >
                  <link.icon className="w-4 h-4" />
                  {link.label}
                </NavLink>
              ))}
            </div>

            {/* At 390px this cluster ran to x=409 in a 390px viewport, so the menu
                button was partly unreachable. Search moves into the mobile menu
                below sm, the gap tightens, and every control is a 44px tap target
                rather than the 36px they were. */}
            <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">

              {/* /api/search was fully implemented but nothing in the UI reached it. */}
              <NavLink
                to="/search"
                className={({ isActive }) =>
                  `hidden sm:flex w-11 h-11 items-center justify-center rounded-full transition-colors ${isActive
                    ? "bg-orange-500 text-black"
                    : "text-white/70 hover:text-white hover:bg-white/10"
                  }`
                }
                aria-label="Search videos"
                data-testid="nav-search"
              >
                <Search className="w-5 h-5" />
              </NavLink>

              <div className="relative">
                <button
                  onClick={() => setShowCountrySelect(!showCountrySelect)}
                  className="flex items-center gap-1.5 sm:gap-2 h-11 px-2.5 sm:px-3 rounded-full bg-white/5 border border-white/10 hover:border-orange-500/50 transition-colors"
                  data-testid="country-selector-btn"
                  aria-label={`Region: ${country.name}`}
                >
                  <span className="text-xl">{country.flag}</span>
                  <span className="hidden sm:inline text-sm font-medium">{country.code}</span>
                  <ChevronDown className="w-4 h-4 text-white/50" />
                </button>

                <AnimatePresence>
                  {showCountrySelect && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute right-0 mt-2 w-64 glass rounded-xl overflow-hidden shadow-2xl"
                      data-testid="country-dropdown"
                    >
                      <div className="p-2 border-b border-white/10">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                          <input
                            type="text"
                            placeholder="Search country..."
                            value={countrySearch}
                            onChange={(e) => setCountrySearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-white/5 rounded-lg text-sm outline-none focus:ring-1 focus:ring-orange-500"
                            data-testid="country-search-input"
                          />
                        </div>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {filteredCountries.map((c) => (
                          <button
                            key={c.code}
                            onClick={() => {
                              setCountry(c);
                              setShowCountrySelect(false);
                              setCountrySearch("");
                              toast.success(`Switched to ${c.name}`);
                            }}
                            className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-white/10 transition-colors ${country.code === c.code ? "bg-orange-500/10 text-orange-500" : ""
                              }`}
                            data-testid={`country-option-${c.code}`}
                          >
                            <span className="text-xl">{c.flag}</span>
                            <span className="text-sm font-medium">{c.name}</span>
                            <span className="text-xs text-white/40 ml-auto">{c.code}</span>
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <NavLink
                to="/account"
                className={({ isActive }) =>
                  `flex w-11 h-11 items-center justify-center rounded-full transition-colors ${isActive
                    ? "bg-orange-500 text-black"
                    : "text-white/70 hover:text-white hover:bg-white/10"
                  }`
                }
                aria-label={isSignedIn ? "Your account" : "Sign in"}
                data-testid="nav-account"
                title={isSignedIn ? "Your account" : "Sign in"}
              >
                <UserCircle className={`w-5 h-5 ${isSignedIn ? "text-orange-400" : ""}`} />
              </NavLink>

              <button
                onClick={() => setIsOpen(!isOpen)}
                className="xl:hidden flex w-11 h-11 items-center justify-center rounded-lg hover:bg-white/10"
                data-testid="mobile-menu-btn"
                aria-label={isOpen ? "Close menu" : "Open menu"}
                aria-expanded={isOpen}
              >
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="xl:hidden glass border-t border-white/10"
            >
              <div className="px-4 py-4 space-y-2">
                {/* Search is included here because its icon is hidden from the bar
                    below sm - otherwise the page becomes unreachable on a phone. */}
                {[...navLinks, { path: "/search", label: "Search", icon: Search }].map((link) => (
                  <NavLink
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 min-h-[44px] py-3 rounded-xl font-medium transition-all ${isActive
                        ? "bg-gradient-to-r from-orange-500 to-yellow-500 text-black"
                        : "text-white/70 hover:bg-white/10"
                      }`
                    }
                  >
                    <link.icon className="w-5 h-5" />
                    {link.label}
                  </NavLink>
                ))}

                {/* The footer already carries this link, but the footer sits below
                    an endless feed, so in practice nobody reaches it. The menu is
                    where someone goes looking for the app. */}
                {!IS_NATIVE && (
                  <a
                    href={PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsOpen(false)}
                    className="mt-2 flex items-center gap-3 rounded-xl border border-orange-500/30 bg-orange-500/10 px-4 min-h-[44px] py-3 font-medium text-orange-300 transition-all hover:bg-orange-500/20"
                  >
                    <Download className="w-5 h-5" />
                    Get the Android app
                  </a>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {showCountrySelect && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowCountrySelect(false);
            setCountrySearch("");
          }}
        />
      )}
    </>
  );
};

// Trending Info Overlay Component
const TrendingInfoOverlay = ({ video, onClose }) => {
  const { country } = useCountry();

  // Build redirect URL for click tracking
  const getRedirectUrl = () => {
    const params = new URLSearchParams({
      country: country.code,
      type: video.platform,
      category: video.category_id || '',
      title: video.title || '',
      channel: video.channel_title || ''
    });
    return `${API}/r/${video.id}?${params.toString()}`;
  };

  // Calculate engagement metrics
  const parseCount = (str) => {
    if (!str) return 0;
    const num = parseFloat(str);
    if (str.includes('B')) return num * 1_000_000_000;
    if (str.includes('M')) return num * 1_000_000;
    if (str.includes('K')) return num * 1_000;
    return num;
  };

  const views = parseCount(video.view_count);
  const likes = parseCount(video.like_count);
  const engagementRate = views > 0 ? ((likes / views) * 100).toFixed(2) : 0;

  // Calculate view velocity (views per hour since publish)
  const getViewVelocity = () => {
    if (!video.published_at) return null;
    const publishDate = new Date(video.published_at);
    const now = new Date();
    const hoursSincePublish = Math.max(1, (now - publishDate) / (1000 * 60 * 60));
    const viewsPerHour = views / hoursSincePublish;

    if (viewsPerHour >= 1_000_000) return `${(viewsPerHour / 1_000_000).toFixed(1)}M/hr`;
    if (viewsPerHour >= 1_000) return `${(viewsPerHour / 1_000).toFixed(1)}K/hr`;
    return `${Math.round(viewsPerHour)}/hr`;
  };

  // Calculate trending score (simplified algorithm)
  const getTrendingScore = () => {
    const viewScore = Math.min(views / 1_000_000, 100) * 0.4;
    const engagementScore = Math.min(engagementRate * 10, 100) * 0.3;
    const recencyBonus = video.trending_rank <= 10 ? 30 : video.trending_rank <= 25 ? 20 : 10;
    return Math.min(100, Math.round(viewScore + engagementScore + recencyBonus));
  };

  const trendingScore = getTrendingScore();
  const viewVelocity = getViewVelocity();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center px-4 pb-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* Content */}
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="relative w-full max-w-md glass rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="trending-info-overlay"
      >
        {/* Header with gradient */}
        <div className="relative p-4 bg-gradient-to-r from-orange-500/20 to-yellow-500/20 border-b border-white/10">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/10 transition-colors"
            data-testid="close-trending-info"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-yellow-500 flex items-center justify-center">
              <Flame className="w-7 h-7 text-black" />
            </div>
            <div>
              <h3 className="font-heading text-lg font-bold text-white">Why It's Trending</h3>
              <p className="text-sm text-white/60">#{video.trending_rank} in {video.platform === 'shorts' ? 'Shorts' : 'YouTube'}</p>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="p-4 space-y-4">
          {/* Trending Score */}
          <div className="p-3 rounded-xl bg-gradient-to-r from-orange-500/10 to-yellow-500/10 border border-orange-500/20">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-white/70 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-orange-500" />
                Trending Score
              </span>
              <span className="text-2xl font-heading font-bold text-orange-500">{trendingScore}</span>
            </div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${trendingScore}%` }}
                transition={{ duration: 1, delay: 0.2 }}
                className="h-full bg-gradient-to-r from-orange-500 to-yellow-500"
              />
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-2 gap-3">
            {/* View Velocity */}
            {viewVelocity && (
              <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-2 text-white/50 text-xs mb-1">
                  <Timer className="w-3.5 h-3.5" />
                  View Velocity
                </div>
                <p className="font-heading text-lg font-bold text-white">{viewVelocity}</p>
              </div>
            )}

            {/* Engagement Rate */}
            <div className="p-3 rounded-xl bg-white/5 border border-white/10">
              <div className="flex items-center gap-2 text-white/50 text-xs mb-1">
                <ThumbsUp className="w-3.5 h-3.5" />
                Engagement
              </div>
              <p className="font-heading text-lg font-bold text-white">{engagementRate}%</p>
            </div>

            {/* Total Views */}
            <div className="p-3 rounded-xl bg-white/5 border border-white/10">
              <div className="flex items-center gap-2 text-white/50 text-xs mb-1">
                <Eye className="w-3.5 h-3.5" />
                Total Views
              </div>
              <p className="font-heading text-lg font-bold text-white">{video.view_count}</p>
            </div>

            {/* Likes */}
            <div className="p-3 rounded-xl bg-white/5 border border-white/10">
              <div className="flex items-center gap-2 text-white/50 text-xs mb-1">
                <ThumbsUp className="w-3.5 h-3.5" />
                Likes
              </div>
              <p className="font-heading text-lg font-bold text-white">{video.like_count || '0'}</p>
            </div>
          </div>

          {/* Video Info */}
          <div className="p-3 rounded-xl bg-white/5 border border-white/10">
            <h4 className="font-medium text-white text-sm mb-2 line-clamp-2">{video.title}</h4>
            <div className="flex items-center gap-2 text-xs text-white/50">
              <span>{video.channel_title}</span>
              <span>•</span>
              <span>{timeAgo(video.published_at)}</span>
            </div>
          </div>

          {/* Open in YouTube */}
          <button
            onClick={() => window.open(getRedirectUrl(), '_blank', 'noopener,noreferrer')}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-orange-500 to-yellow-500 text-black font-heading font-bold text-sm hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
            data-testid="open-in-youtube-btn"
          >
            <Youtube className="w-5 h-5" />
            Watch on YouTube
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Video Card Component
const VideoCard = ({ video, index, onVideoEnd }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showTrendingInfo, setShowTrendingInfo] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasTrackedClick, setHasTrackedClick] = useState(false);
  const [hasEverPlayed, setHasEverPlayed] = useState(false); // Track if video ever started playing
  const cardRef = useRef(null);
  const iframeRef = useRef(null);
  const videoContainerRef = useRef(null);
  const { isMuted, activeVideoId, setActiveVideoId } = useAudio();
  const { country } = useCountry();
  const { play } = usePlayer();
  const shouldAutoplay = useSingleVideoAutoplay(video.id, cardRef, isHovered);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [saved, setSaved] = useState(() => isVideoSaved(video.id));
  const hasAppliedMuteRef = useRef(false);

  // Mark video as played when it starts autoplaying
  useEffect(() => {
    if (shouldAutoplay && !hasEverPlayed) {
      setHasEverPlayed(true);
    }
  }, [shouldAutoplay, hasEverPlayed]);

  // Determine if iframe should be visible:
  // - Show if shouldAutoplay (currently the active video)
  // - OR if hasEverPlayed AND this is still the activeVideoId (user is interacting)
  const shouldShowIframe = shouldAutoplay || (hasEverPlayed && activeVideoId === video.id);

  // Reset states when a DIFFERENT video becomes active
  useEffect(() => {
    if (activeVideoId && activeVideoId !== video.id) {
      // Another video became active, reset this one
      setHasEverPlayed(false);
      setHasTrackedClick(false);
      setIframeLoaded(false);
    }
  }, [activeVideoId, video.id]);

  // Build redirect URL for click tracking
  const getRedirectUrl = useCallback(() => {
    const params = new URLSearchParams({
      country: country.code,
      type: video.platform,
      category: video.category_id || '',
      title: video.title || '',
      channel: video.channel_title || ''
    });
    return `${API}/r/${video.id}?${params.toString()}`;
  }, [video.id, video.platform, video.category_id, video.title, video.channel_title, country.code]);

  // Open the video in the in-app player rather than handing the visitor to YouTube.
  // The click is still recorded, and the modal keeps a "Watch on YouTube" button that
  // goes through the same tracking redirect - so attribution is unchanged while the
  // session stays on the site.
  const handleVideoClick = useCallback((e) => {
    e.stopPropagation();

    if (!hasTrackedClick) {
      setHasTrackedClick(true);
      // Fire-and-forget: the redirect endpoint logs the click. keepalive lets it
      // survive if the visitor navigates immediately.
      fetch(getRedirectUrl(), { method: "GET", mode: "no-cors", keepalive: true }).catch(() => {});
    }

    recordWatch(video);
    play(video);
  }, [hasTrackedClick, getRedirectUrl, video, play]);

  // YouTube embed URL - controls=1 for timeline, fs=1 for fullscreen button
  const getEmbedUrl = useCallback(() => {
    const videoId = video.id;
    const params = [
      "autoplay=1",
      "mute=1",
      "loop=1",
      `playlist=${videoId}`,
      "controls=1",
      "showinfo=0",
      "modestbranding=1",
      // Without this iOS hands playback to the fullscreen system player.
      "playsinline=1",
      "rel=0",
      "fs=1",
      "enablejsapi=1",
    ];

    // The origin parameter has to be a real http(s) origin. In the packaged apps
    // the page is served from a custom scheme - capacitor://localhost on iOS - and
    // handing that to the player API makes it reject the embed. Omitting it is
    // allowed; the postMessage calls below still work because they target "*".
    if (!IS_NATIVE) params.push(`origin=${window.location.origin}`);

    return ytEmbedUrl(videoId, params.join("&"));
  }, [video.id]);

  // Control YouTube iframe mute/unmute via postMessage API
  useEffect(() => {
    if (iframeRef.current && iframeLoaded && shouldShowIframe) {
      const command = isMuted ? 'mute' : 'unMute';
      try {
        iframeRef.current.contentWindow?.postMessage(
          JSON.stringify({ event: 'command', func: command }),
          '*'
        );
      } catch (e) {
        console.warn('Failed to send postMessage to iframe:', e);
      }
    }
  }, [isMuted, iframeLoaded, shouldShowIframe]);

  // Listen for video end messages from YouTube iframe
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== YT_MESSAGE_ORIGIN) return;
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        // YouTube player state: 0 = ended
        if (data.event === 'onStateChange' && data.info === 0 && shouldAutoplay) {
          onVideoEnd?.(video.id);
        }
      } catch (e) {
        // Ignore non-JSON messages
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [shouldAutoplay, onVideoEnd, video.id]);

  const handleIframeLoad = useCallback(() => {
    setIframeLoaded(true);
    hasAppliedMuteRef.current = false;
  }, []);

  // Apply initial mute state after iframe loads - separate effect for initial setup
  useEffect(() => {
    if (iframeRef.current && iframeLoaded && shouldShowIframe && !hasAppliedMuteRef.current) {
      const timer = setTimeout(() => {
        try {
          const command = isMuted ? 'mute' : 'unMute';
          iframeRef.current?.contentWindow?.postMessage(
            JSON.stringify({ event: 'command', func: command }),
            '*'
          );
          hasAppliedMuteRef.current = true;
        } catch (e) {
          console.warn('Failed to apply initial mute state:', e);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [iframeLoaded, shouldShowIframe, isMuted]);

  // Track clicks when user interacts with YouTube iframe and navigates to YouTube
  // This detects when user clicks YouTube logo or "Watch on YouTube" in the player
  useEffect(() => {
    if (!shouldAutoplay || !iframeLoaded) return;

    const handleWindowBlur = () => {
      // When iframe causes focus to leave (user clicked YouTube link in player)
      // Track this as a click if we haven't already
      if (document.activeElement === iframeRef.current && !hasTrackedClick) {
        setHasTrackedClick(true);
        // Send tracking request in background (non-blocking)
        const trackUrl = `${API}/r/${video.id}?country=${country.code}&type=${video.platform}&title=${encodeURIComponent(video.title || '')}&channel=${encodeURIComponent(video.channel_title || '')}`;
        fetch(trackUrl, { method: 'GET', mode: 'no-cors' }).catch(() => { });
      }
    };

    window.addEventListener('blur', handleWindowBlur);
    return () => window.removeEventListener('blur', handleWindowBlur);
  }, [shouldAutoplay, iframeLoaded, hasTrackedClick, video.id, video.platform, video.title, video.channel_title, country.code]);

  // Send heartbeat when video is playing (for "watching now" count)
  useEffect(() => {
    if (shouldAutoplay) {
      const sendHeartbeat = () => {
        axios.post(`${API}/activity/heartbeat?video_id=${video.id}&country=${country.code}`)
          .catch(() => { }); // Silently fail
      };

      sendHeartbeat(); // Send immediately
      const interval = setInterval(sendHeartbeat, 10000); // Then every 10 seconds
      return () => clearInterval(interval);
    }
  }, [shouldAutoplay, video.id, country.code]);

  // Handle fullscreen toggle - enter or exit fullscreen
  const handleFullscreen = (e) => {
    e.stopPropagation();
    e.preventDefault();

    // If already in fullscreen, exit
    if (document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      return;
    }

    // Enter fullscreen
    const iframe = iframeRef.current;
    if (iframe) {
      // Try to request fullscreen on the iframe
      if (iframe.requestFullscreen) {
        iframe.requestFullscreen().catch(err => {
          console.warn('Iframe fullscreen failed, trying container:', err);
          // Fallback to container
          if (videoContainerRef.current?.requestFullscreen) {
            videoContainerRef.current.requestFullscreen().catch(e => console.warn('Container fullscreen failed:', e));
          }
        });
      } else if (iframe.webkitRequestFullscreen) {
        iframe.webkitRequestFullscreen();
      } else if (iframe.mozRequestFullScreen) {
        iframe.mozRequestFullScreen();
      } else if (iframe.msRequestFullscreen) {
        iframe.msRequestFullscreen();
      }
    } else if (videoContainerRef.current) {
      // Fallback to container if iframe not available
      videoContainerRef.current.requestFullscreen?.().catch(err => {
        console.warn('Fullscreen failed:', err);
      });
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const handleKnowMoreClick = (e) => {
    e.stopPropagation();
    setShowTrendingInfo(true);
  };

  return (
    <>
      <motion.div
        ref={cardRef}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: Math.min(index * 0.03, 0.3) }}
        className={`video-card glass-card rounded-xl overflow-hidden group cursor-pointer relative ${shouldAutoplay ? 'video-active' : ''}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleVideoClick}
        data-testid={`video-card-${video.id}`}
      >
        {/* Trending Rank Badge - only show if rank > 0 and <= 20 */}
        {video.trending_rank > 0 && video.trending_rank <= 20 && (
          <div className="absolute top-2 right-2 z-20 flex items-center gap-1 px-2 py-1 rounded-lg font-heading font-bold text-sm bg-black/70 text-white/90" data-testid={`trending-rank-${video.id}`}>
            <Flame className="w-3.5 h-3.5 text-orange-500" />
            #{video.trending_rank}
            {/* How far it moved since the last snapshot */}
            <RankMovement video={video} />
          </div>
        )}

        <div ref={videoContainerRef} className={`relative aspect-video overflow-hidden bg-black ${isFullscreen ? 'fullscreen-video' : ''}`}>
          {/* Thumbnail - show when video is not playing and not interacting */}
          {!shouldShowIframe && (
            <img
              src={video.thumbnail_high || video.thumbnail_url}
              alt={video.title}
              className={`absolute inset-0 w-full h-full object-cover transition-all duration-300 ${isHovered ? 'scale-110' : 'scale-100'}`}
              loading="lazy"
            />
          )}

          {/* YouTube iframe - render when autoplay OR user is interacting */}
          {shouldShowIframe && (
            <iframe
              ref={iframeRef}
              key={`${video.id}-active`}
              src={getEmbedUrl()}
              title={video.title}
              className="absolute inset-0 w-full h-full z-20"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
              style={{ border: 0 }}
              onLoad={handleIframeLoad}
            />
          )}

          {/* Platform Icon */}
          <div className="absolute top-2 left-2 z-10">
            <PlatformIcon platform={video.platform} isPlaying={shouldAutoplay} />
          </div>

          {/* Duration badge - hide when video is playing (YouTube shows its own) */}
          {!shouldAutoplay && (
            <div className="absolute bottom-2 right-2 z-10 px-1.5 py-0.5 rounded bg-black/80 text-xs font-mono text-white">
              {video.duration}
            </div>
          )}

          {/* Play button on hover when not playing */}
          {!shouldAutoplay && isHovered && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 z-20">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-500 to-yellow-500 flex items-center justify-center">
                <Play className="w-7 h-7 text-black fill-black ml-0.5" />
              </div>
            </div>
          )}

          {/* Watching Now Badge - shows on hover with light background */}
          {isHovered && (
            <WatchingNowHoverBadge videoId={video.id} />
          )}
        </div>

        {/* Info section with channel avatar and stats */}
        <div className="p-3 sm:p-4">
          <div className="flex gap-3">
            {/* Channel Avatar */}
            <div className="flex-shrink-0">
              {video.channel_avatar ? (
                <img
                  src={video.channel_avatar}
                  alt={video.channel_title}
                  className="w-9 h-9 rounded-full object-cover"
                />
              ) : (
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-500 to-yellow-500 flex items-center justify-center text-black font-bold text-sm">
                  {video.channel_title?.charAt(0)?.toUpperCase() || 'C'}
                </div>
              )}
            </div>

            {/* Title and Channel Info */}
            <div className="flex-1 min-w-0">
              <h3 className="font-heading text-sm font-bold line-clamp-2 mb-1 text-white group-hover:text-orange-400 transition-colors">
                {video.title}
              </h3>
              <div className="flex items-center gap-1 text-xs text-white/50 mb-1.5">
                <span className="truncate">{video.channel_title}</span>
                {video.subscriber_count && (
                  <>
                    <span className="text-white/30">•</span>
                    <span className="flex items-center gap-0.5 flex-shrink-0">
                      <Users className="w-3 h-3" />
                      {video.subscriber_count}
                    </span>
                  </>
                )}
                <span onClick={(e) => e.stopPropagation()} className="ml-auto flex-shrink-0">
                  <FollowButton
                    channel={{
                      channel_id: video.channel_id,
                      channel_title: video.channel_title,
                      channel_avatar: video.channel_avatar,
                    }}
                  />
                </span>
              </div>

              {/* Stats Row */}
              <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-white/40">
                <span className="flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  {video.view_count}
                </span>
                {video.like_count && video.like_count !== "0" && (
                  <span className="flex items-center gap-1">
                    <ThumbsUp className="w-3 h-3" />
                    {video.like_count}
                  </span>
                )}
                {video.published_at && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {timeAgo(video.published_at)}
                  </span>
                )}
              </div>

              {/* Movement against the last stored snapshot. Renders nothing until
                  snapshots exist, so this is blank on a fresh install. */}
              <VelocityBadge video={video} className="mt-1" />

              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <button
                  onClick={handleKnowMoreClick}
                  className="px-2 py-1 rounded-md bg-orange-500/10 text-xs font-medium text-orange-400 hover:bg-orange-500/20 transition-colors flex items-center gap-1 border border-orange-500/20 w-fit"
                  data-testid={`know-more-btn-${video.id}`}
                >
                  <Info className="w-3 h-3" />
                  Why Trending?
                </button>

                {/* The card itself opens the modal, which keeps the visitor in the
                    feed. This is the way to the video's own page - a URL that can
                    be shared, bookmarked and indexed. */}
                <NavLink
                  to={`/video/${video.id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="px-2 py-1 rounded-md text-xs font-medium text-white/50 hover:text-orange-400 transition-colors flex items-center gap-1"
                  data-testid={`video-page-link-${video.id}`}
                >
                  <ArrowUpRight className="w-3 h-3" />
                  Details
                </NavLink>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSaved(toggleVideoSaved(video));
                  }}
                  className="px-2 py-1 rounded-md text-xs font-medium text-white/50 hover:text-orange-400 transition-colors flex items-center gap-1"
                  aria-label={saved ? "Remove from saved" : "Save video"}
                >
                  <Bookmark className={`w-3 h-3 ${saved ? "fill-orange-400 text-orange-400" : ""}`} />
                  {saved ? "Saved" : "Save"}
                </button>

                <ShareMenu video={video} />
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Trending Info Overlay */}
      <AnimatePresence>
        {showTrendingInfo && (
          <TrendingInfoOverlay
            video={video}
            onClose={() => setShowTrendingInfo(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

// Video Grid Component
const VideoGrid = ({ videos, loading, loadingMore, hasMore, lastElementRef }) => {
  const { setActiveVideoId } = useAudio();
  const videoRefs = useRef({});

  // Handle video end - auto-play next video in list
  const handleVideoEnd = useCallback((endedVideoId) => {
    const currentIndex = videos.findIndex(v => v.id === endedVideoId);
    if (currentIndex >= 0 && currentIndex < videos.length - 1) {
      const nextVideo = videos[currentIndex + 1];
      // Set next video as active to trigger autoplay
      setActiveVideoId(nextVideo.id);
      // Scroll to next video smoothly
      const nextVideoElement = videoRefs.current[nextVideo.id];
      if (nextVideoElement) {
        nextVideoElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [videos, setActiveVideoId]);

  if (loading && videos.length === 0) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="glass-card rounded-xl overflow-hidden">
            <div className="aspect-video skeleton" />
            <div className="p-4 space-y-3">
              <div className="h-4 skeleton rounded w-full" />
              <div className="h-4 skeleton rounded w-3/4" />
              <div className="h-3 skeleton rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!videos.length) {
    return (
      <div className="text-center py-20">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-white/5 flex items-center justify-center">
          <Film className="w-10 h-10 text-white/30" />
        </div>
        <h3 className="font-heading text-xl mb-2">No videos found</h3>
        <p className="text-white/50">Try changing the country or category</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="video-grid">
        {videos.map((video, index) => (
          <div key={`${video.id}-${index}`} ref={el => videoRefs.current[video.id] = el}>
            <VideoCard
              video={video}
              index={index}
              onVideoEnd={handleVideoEnd}
            />
          </div>
        ))}
      </div>

      {hasMore && !loadingMore && (
        <div
          ref={lastElementRef}
          className="h-20 flex items-center justify-center"
          data-testid="infinite-scroll-trigger"
        >
          <span className="text-white/30 text-sm">Scroll for more...</span>
        </div>
      )}

      {loadingMore && (
        <div className="flex justify-center items-center py-8" data-testid="loading-more">
          <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
          <span className="ml-3 text-white/60">Loading more videos...</span>
        </div>
      )}

      {!hasMore && videos.length > 0 && (
        <div className="text-center py-8 text-white/40">
          <p>You've reached the end</p>
        </div>
      )}
    </>
  );
};

// Genre Filter Component (for Music category) - one scrolling row on mobile
const GenreFilter = ({ genres, activeGenre, onGenreChange }) => {
  if (!genres || genres.length === 0) return null;


  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      className="mb-2 sm:mb-4"
      data-testid="genre-filter"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Music className="w-4 h-4 text-purple-400 flex-shrink-0" />
        <span className="text-xs sm:text-sm text-white/60">Genre</span>
      </div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap sm:overflow-visible">
        {genres.map((genre) => (
          <button
            key={genre.id}
            onClick={() => onGenreChange(genre.id)}
            className={`px-3 h-11 sm:h-auto sm:py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap flex-shrink-0 ${activeGenre === genre.id
              ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white"
              : "bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10"
              }`}
            data-testid={`genre-${genre.id}`}
          >
            {genre.title}
          </button>
        ))}
      </div>
    </motion.div>
  );
};

// Language Filter Component (for all categories) - one scrolling row on mobile
const LanguageFilter = ({ languages, activeLanguage, onLanguageChange }) => {
  if (!languages || languages.length === 0) return null;


  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      className="mb-2 sm:mb-4"
      data-testid="language-filter"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Globe className="w-4 h-4 text-blue-400 flex-shrink-0" />
        <span className="text-xs sm:text-sm text-white/60">Language</span>
      </div>
      {/* One scrolling line, same as the categories above. Twenty-odd languages
          wrapped into three stacked rows on a desktop, which pushed the feed
          itself below the fold. */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 -mx-4 px-4 sm:mx-0 sm:px-0">
        {languages.map((language) => (
          <button
            key={language.id}
            onClick={() => onLanguageChange(language.id)}
            className={`px-3 h-11 sm:h-auto sm:py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap flex-shrink-0 ${activeLanguage === language.id
              ? "bg-gradient-to-r from-blue-500 to-cyan-500 text-white"
              : "bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10"
              }`}
            data-testid={`language-${language.id}`}
          >
            {language.title}
          </button>
        ))}
      </div>
    </motion.div>
  );
};

// Category Tabs Component
const CategoryTabs = ({ categories, activeCategory, onCategoryChange }) => {
  const categoryIcons = {
    "0": Flame,
    "10": Music,
    "20": Gamepad2,
    "24": Film,
    "17": TrendingUp,
    "28": Sparkles,
    "25": Newspaper,
  };

  return (
    // One scrolling line at every width. It used to wrap above sm, which on a
    // desktop turned ten categories into two stacked rows of chips above the
    // feed - the same clutter the horizontal scroll was added to avoid on
    // phones, just with more room to spread out. Wrapping also cancels the
    // horizontal scroll the container already asks for.
    <div className="flex gap-2 mb-4 sm:mb-6 overflow-x-auto no-scrollbar pb-1 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="category-tabs">
      {categories.map((cat) => {
        const Icon = categoryIcons[cat.id] || Film;
        return (
          <button
            key={cat.id}
            onClick={() => onCategoryChange(cat.id)}
            className={`flex items-center gap-1.5 px-3 sm:px-4 sm:py-2 h-11 sm:h-auto rounded-full text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 ${activeCategory === cat.id
              ? "bg-gradient-to-r from-orange-500 to-yellow-500 text-black"
              : "bg-white/5 text-white/70 hover:bg-white/10 hover:text-white"
              }`}
            data-testid={`category-${cat.id}`}
          >
            <Icon className="w-4 h-4" />
            <span className="hidden sm:inline">{cat.title}</span>
            <span className="sm:hidden">{cat.title.split(' ')[0]}</span>
          </button>
        );
      })}
    </div>
  );
};

// Every filter the home feed understands. Anything left at its default is kept out
// of the query string, so a plain "/" stays a plain "/".
const HOME_FILTER_DEFAULTS = {
  category: "0",
  genre: "all",
  language: "all",
  platform: "all",
  duration: "any",
  published_within: "any",
  min_views: 0,
  sort_by: "trending",
};

// Home Page Component
const HomePage = () => {
  const { country, setCountry, countries } = useCountry();
  // Route params only appear on the /trending/:countryCode landing pages.
  const { countryCode, categoryParam } = useParams();
  const { filters, setFilter, resetFilters, activeCount } = useFilterState(HOME_FILTER_DEFAULTS);

  const activeCategory = categoryParam || filters.category;
  const { genre: activeGenre, language: activeLanguage, platform: activePlatform } = filters;

  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [categories, setCategories] = useState([]);
  const [genres, setGenres] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [pageToken, setPageToken] = useState(null);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [notice, setNotice] = useState(null);

  // A /trending/us URL should actually switch the region, not just look like it.
  useEffect(() => {
    if (!countryCode || !countries.length) return;
    const match = countries.find((c) => c.code === countryCode.toUpperCase());
    if (match && match.code !== country.code) setCountry(match);
  }, [countryCode, countries, country.code, setCountry]);

  // Fetch categories, genres, and languages
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [catRes, genreRes, langRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/genres`),
          axios.get(`${API}/languages`)
        ]);
        setCategories(catRes.data);
        setGenres(genreRes.data);
        setLanguages(langRes.data);
      } catch (e) {
        console.error("Failed to fetch filters:", e);
      }
    };
    fetchFilters();
  }, []);

  // Reset genre when category changes
  const handleCategoryChange = (categoryId) => {
    setFilter({ category: categoryId, genre: "all" });
  };

  // Fetch videos based on platform, category, genre, and language selection
  useEffect(() => {
    // Region auto-detection changes country.code a moment after mount, so two
    // requests are in flight at once. Without this guard the slower (stale) one
    // wins the race and the grid ends up showing India under a "United States"
    // heading. Anything that arrives after the filters moved on is discarded.
    let current = true;

    const fetchTrending = async () => {
      setLoading(true);
      setVideos([]);
      setPageToken(null);
      setCurrentOffset(0);
      setNotice(null);
      try {
        // The language filter applies to every platform, so it is always sent.
        let endpoint = `${API}/trending/youtube`;
        let params = {
          country: country.code,
          max_results: 24,
          offset: 0,
          language: activeLanguage,
          duration: filters.duration,
          published_within: filters.published_within,
          min_views: filters.min_views,
          sort_by: filters.sort_by,
        };

        if (activePlatform === "shorts") {
          endpoint = `${API}/trending/shorts`;
        } else if (activePlatform === "youtube" || activePlatform === "all") {
          // Use music endpoint if Music category is selected
          if (activeCategory === "10") {
            endpoint = `${API}/trending/music`;
            params.genre = activeGenre;
          } else {
            params.category_id = activeCategory;
          }
        }

        const res = await axios.get(endpoint, { params });
        if (!current) return;
        setVideos(res.data.videos);
        setPageToken(res.data.next_page_token);
        setCurrentOffset(res.data.videos.length);
        setNotice(res.data.notice || null);
      } catch (e) {
        if (!current) return;
        console.error("Failed to fetch trending:", e);
        if (e.response?.status === 429) {
          // Distinct from a network failure: retrying will not help until the
          // YouTube daily quota resets.
          setNotice("Daily YouTube API quota reached — trending data will return once it resets.");
        } else {
          toast.error("Failed to load trending videos");
        }
      } finally {
        if (current) setLoading(false);
      }
    };
    fetchTrending();
    return () => {
      current = false;
    };
  }, [
    country.code,
    activeCategory,
    activeGenre,
    activeLanguage,
    activePlatform,
    filters.duration,
    filters.published_within,
    filters.min_views,
    filters.sort_by,
  ]);

  const loadMore = useCallback(async () => {
    if (!pageToken || loadingMore) return;
    setLoadingMore(true);
    try {
      let endpoint = `${API}/trending/youtube`;
      let params = {
        country: country.code,
        max_results: 12,
        page_token: pageToken,
        offset: currentOffset,
        language: activeLanguage,
        duration: filters.duration,
        published_within: filters.published_within,
        min_views: filters.min_views,
        sort_by: filters.sort_by,
      };

      if (activePlatform === "shorts") {
        endpoint = `${API}/trending/shorts`;
      } else if (activePlatform === "youtube" || activePlatform === "all") {
        if (activeCategory === "10") {
          endpoint = `${API}/trending/music`;
          params.genre = activeGenre;
        } else {
          params.category_id = activeCategory;
        }
      }

      const res = await axios.get(endpoint, { params });
      // The chart and search streams can surface the same video, so drop anything
      // already on screen rather than rendering it twice.
      setVideos((prev) => {
        const seen = new Set(prev.map((v) => v.id));
        return [...prev, ...res.data.videos.filter((v) => !seen.has(v.id))];
      });
      setPageToken(res.data.next_page_token);
      setCurrentOffset((prev) => prev + res.data.videos.length);
    } catch (e) {
      console.error("Failed to load more:", e);
    } finally {
      setLoadingMore(false);
    }
  }, [
    pageToken,
    loadingMore,
    country.code,
    activeCategory,
    activeGenre,
    activeLanguage,
    activePlatform,
    currentOffset,
    filters.duration,
    filters.published_within,
    filters.min_views,
    filters.sort_by,
  ]);

  const lastElementRef = useInfiniteScroll(loadMore, !!pageToken);

  // A short page can leave the sentinel above the fold, so the observer never fires
  // and the feed stalls even though more videos are available. Keep pulling until
  // the page is actually scrollable.
  useEffect(() => {
    if (loading || loadingMore || !pageToken) return;
    if (document.documentElement.scrollHeight <= window.innerHeight + 200) {
      loadMore();
    }
  }, [videos, loading, loadingMore, pageToken, loadMore]);

  return (
    <div className="min-h-screen pt-20" data-testid="home-page">
      <section className="relative py-5 sm:py-16 px-4 hero-gradient noise-overlay overflow-hidden">
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-orange-500/10 border border-orange-500/30 mb-3 sm:mb-6">
              <Globe className="w-3 h-3 sm:w-4 sm:h-4 text-orange-500" />
              <span className="text-xs sm:text-sm font-medium">Trending in {country.name}</span>
            </div>
            <h1 className="font-heading text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter mb-2 sm:mb-4 px-2">
              What's <span className="text-orange-500 neon-text">Trending</span> Now
            </h1>
            <p className="text-xs sm:text-base md:text-lg text-white/60 max-w-2xl mx-auto mb-3 sm:mb-4 px-4">
              Top 20 trending videos from YouTube & Shorts.
            </p>

            {/* The Play link lives here rather than in the nav bar. The bar is
                full: eight inline links plus search, country and account leave no
                slack, and adding even a 44px icon truncated the wordmark to
                "Trendin..." and wrapped "Top Clicks" onto two lines - at 1400 and
                still at 1600. The hero has room, and it is the first thing on the
                page instead of the last. The menu and footer keep their copies. */}
            {!IS_NATIVE && (
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mb-4 inline-flex items-center gap-2 rounded-full border border-orange-500/40 bg-orange-500/10 px-4 py-2 text-xs font-semibold text-orange-300 transition-colors hover:bg-orange-500/20 sm:text-sm"
                data-testid="hero-get-app"
              >
                <Download className="h-4 w-4 shrink-0" />
                Get the Android app
              </a>
            )}

            {/* Live Activity Map */}
            <div className="max-w-md mx-auto mb-2 sm:mb-6">
              <p className="text-xs text-white/40 mb-2 flex items-center justify-center gap-1">
                <Activity className="w-3 h-3 text-orange-500 animate-pulse" />
                Live Activity
              </p>
              <GlobalActivityMap />
            </div>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-4 sm:py-12">
        <div className="flex items-center justify-between mb-4 sm:mb-8">
          <h2 className="font-heading text-xl sm:text-2xl font-bold flex items-center gap-2 sm:gap-3">
            <Flame className="w-5 h-5 sm:w-6 sm:h-6 text-orange-500" />
            <span className="hidden sm:inline">
              {activePlatform === "shorts" ? "Trending Shorts" : "Trending on YouTube"}
            </span>
            <span className="sm:hidden">Trending</span>
          </h2>
        </div>

        {activePlatform !== "shorts" && (
          <>
            <CategoryTabs
              categories={categories}
              activeCategory={activeCategory}
              onCategoryChange={handleCategoryChange}
            />

            {/* Show Genre Filter when Music category is selected */}
            <AnimatePresence>
              {activeCategory === "10" && (
                <GenreFilter
                  genres={genres}
                  activeGenre={activeGenre}
                  onGenreChange={(genre) => setFilter({ genre })}
                />
              )}
            </AnimatePresence>

            {/* Show Language Filter for all categories */}
            <AnimatePresence>
              <LanguageFilter
                languages={languages}
                activeLanguage={activeLanguage}
                onLanguageChange={(language) => setFilter({ language })}
              />
            </AnimatePresence>
          </>
        )}

        <PlatformFilter
          activePlatform={activePlatform}
          onPlatformChange={(platform) => setFilter({ platform })}
        />

        <AdvancedFilters
          filters={filters}
          setFilter={setFilter}
          resetFilters={resetFilters}
          activeCount={activeCount}
        />

        {/* Tells the user when the chart had nothing for their filters, instead of
            silently passing off keyword-search results as trending. */}
        {notice && !loading && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-start gap-2 mb-6 px-4 py-3 rounded-xl bg-orange-500/10 border border-orange-500/30"
            data-testid="filter-notice"
          >
            <Info className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
            <p className="text-xs sm:text-sm text-white/70">{notice}</p>
          </motion.div>
        )}

        <VideoGrid
          videos={videos}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={!!pageToken}
          lastElementRef={lastElementRef}
        />

        <div className="max-w-md mx-auto mt-16">
          <DigestSignup />
        </div>
      </section>
    </div>
  );
};

const SHORTS_FILTER_DEFAULTS = { language: "all" };

// Shorts Page Component
const ShortsPage = () => {
  const { country } = useCountry();
  // The /shorts route ignored the language filter entirely, so it disagreed with the
  // Shorts tab on the home page. Both now read the same filter.
  const { filters, setFilter } = useFilterState(SHORTS_FILTER_DEFAULTS);
  const activeLanguage = filters.language;

  const [videos, setVideos] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageToken, setPageToken] = useState(null);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/languages`)
      .then((res) => setLanguages(res.data))
      .catch(() => setLanguages([]));
  }, []);

  useEffect(() => {
    // See the note on the home feed: region detection means two requests can be
    // in flight, and the stale one must not win.
    let current = true;

    const fetchShorts = async () => {
      setLoading(true);
      setVideos([]);
      setPageToken(null);
      setCurrentOffset(0);
      try {
        const res = await axios.get(`${API}/trending/shorts`, {
          params: {
            country: country.code,
            language: activeLanguage,
            max_results: 24,
            offset: 0,
          },
        });
        if (!current) return;
        setVideos(res.data.videos);
        setPageToken(res.data.next_page_token);
        setCurrentOffset(res.data.videos.length);
        setNotice(res.data.notice || null);
      } catch (e) {
        if (!current) return;
        console.error("Failed to fetch shorts:", e);
        toast.error("Failed to load shorts");
      } finally {
        if (current) setLoading(false);
      }
    };
    fetchShorts();
    return () => {
      current = false;
    };
  }, [country.code, activeLanguage]);

  const loadMore = useCallback(async () => {
    if (!pageToken || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await axios.get(`${API}/trending/shorts`, {
        params: {
          country: country.code,
          language: activeLanguage,
          max_results: 12,
          page_token: pageToken,
          offset: currentOffset,
        },
      });
      setVideos((prev) => [...prev, ...res.data.videos]);
      setPageToken(res.data.next_page_token);
      setCurrentOffset((prev) => prev + res.data.videos.length);
    } catch (e) {
      console.error("Failed to load more:", e);
    } finally {
      setLoadingMore(false);
    }
  }, [pageToken, loadingMore, country.code, activeLanguage, currentOffset]);

  const lastElementRef = useInfiniteScroll(loadMore, !!pageToken);

  return (
    <div className="min-h-screen pt-20" data-testid="shorts-page">
      <section className="relative py-12 px-4 hero-gradient noise-overlay">
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-4">
              <span className="text-yellow-500">Shorts</span> & Reels
            </h1>
            <p className="text-lg text-white/60 max-w-xl">
              Trending short-form videos in {country.name}.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-12">
        <LanguageFilter
          languages={languages}
          activeLanguage={activeLanguage}
          onLanguageChange={(language) => setFilter({ language })}
        />

        {/* Says so when the list is not a regional ranking, rather than letting the
            heading claim a country these results do not come from. */}
        {notice && !loading && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-start gap-2 mb-6 px-4 py-3 rounded-xl bg-orange-500/10 border border-orange-500/30"
            data-testid="shorts-notice"
          >
            <Info className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
            <p className="text-xs sm:text-sm text-white/70">{notice}</p>
          </motion.div>
        )}

        <VideoGrid
          videos={videos}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={!!pageToken}
          lastElementRef={lastElementRef}
        />
      </section>
    </div>
  );
};

// Categories Page Component
const CategoriesPage = () => {
  const { country } = useCountry();
  const [categories, setCategories] = useState([]);
  const [genres, setGenres] = useState([]);
  const [activeCategory, setActiveCategory] = useState("10");
  const [activeGenre, setActiveGenre] = useState("all");
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageToken, setPageToken] = useState(null);
  const [currentOffset, setCurrentOffset] = useState(0);

  useEffect(() => {
    const fetchCategoriesAndGenres = async () => {
      try {
        const [catRes, genreRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/genres`)
        ]);
        setCategories(catRes.data.filter(c => c.id !== "0"));
        setGenres(genreRes.data);
      } catch (e) {
        console.error("Failed to fetch categories/genres:", e);
      }
    };
    fetchCategoriesAndGenres();
  }, []);

  // Reset genre when category changes
  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    setActiveGenre("all");
  };

  useEffect(() => {
    // See the note on the home feed: region detection means two requests can be
    // in flight, and the stale one must not win.
    let current = true;

    const fetchVideos = async () => {
      setLoading(true);
      setVideos([]);
      setPageToken(null);
      setCurrentOffset(0);
      try {
        let endpoint = `${API}/trending/youtube`;
        let params = {
          country: country.code,
          max_results: 24,
          offset: 0,
        };

        // Use music endpoint if Music category is selected
        if (activeCategory === "10") {
          endpoint = `${API}/trending/music`;
          params.genre = activeGenre;
        } else {
          params.category_id = activeCategory;
        }

        const res = await axios.get(endpoint, { params });
        if (!current) return;
        setVideos(res.data.videos);
        setPageToken(res.data.next_page_token);
        setCurrentOffset(res.data.videos.length);
      } catch (e) {
        if (!current) return;
        console.error("Failed to fetch videos:", e);
        toast.error("Failed to load videos");
      } finally {
        if (current) setLoading(false);
      }
    };
    fetchVideos();
    return () => {
      current = false;
    };
  }, [country.code, activeCategory, activeGenre]);

  const loadMore = useCallback(async () => {
    if (!pageToken || loadingMore) return;
    setLoadingMore(true);
    try {
      let endpoint = `${API}/trending/youtube`;
      let params = {
        country: country.code,
        max_results: 12,
        page_token: pageToken,
        offset: currentOffset,
      };

      if (activeCategory === "10") {
        endpoint = `${API}/trending/music`;
        params.genre = activeGenre;
      } else {
        params.category_id = activeCategory;
      }

      const res = await axios.get(endpoint, { params });
      // The chart and search streams can surface the same video, so drop anything
      // already on screen rather than rendering it twice.
      setVideos((prev) => {
        const seen = new Set(prev.map((v) => v.id));
        return [...prev, ...res.data.videos.filter((v) => !seen.has(v.id))];
      });
      setPageToken(res.data.next_page_token);
      setCurrentOffset((prev) => prev + res.data.videos.length);
    } catch (e) {
      console.error("Failed to load more:", e);
    } finally {
      setLoadingMore(false);
    }
  }, [pageToken, loadingMore, country.code, activeCategory, activeGenre, currentOffset]);

  const lastElementRef = useInfiniteScroll(loadMore, !!pageToken);

  const categoryIcons = {
    "10": Music,
    "20": Gamepad2,
    "24": Film,
    "17": TrendingUp,
    "28": Sparkles,
    "22": Film,
    "1": Film,
    "25": Newspaper,
    "26": Sparkles,
  };

  return (
    <div className="min-h-screen pt-20" data-testid="categories-page">
      <section className="relative py-12 px-4 hero-gradient noise-overlay">
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-4">
              Browse by <span className="text-yellow-500">Category</span>
            </h1>
            <p className="text-lg text-white/60 max-w-xl">
              Trending videos by category in {country.name}.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-8">
          {categories.map((cat) => {
            const Icon = categoryIcons[cat.id] || Film;
            return (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                className={`p-6 rounded-xl text-center transition-all ${activeCategory === cat.id
                  ? "bg-gradient-to-br from-orange-500 to-yellow-500 text-black"
                  : "glass-card hover:border-orange-500/50"
                  }`}
                data-testid={`category-card-${cat.id}`}
              >
                <Icon className={`w-8 h-8 mx-auto mb-3 ${activeCategory === cat.id ? "text-black" : "text-orange-500"}`} />
                <span className="font-heading text-sm font-bold">{cat.title}</span>
              </button>
            );
          })}
        </div>

        {/* Show Genre Filter when Music category is selected */}
        <AnimatePresence>
          {activeCategory === "10" && (
            <GenreFilter
              genres={genres}
              activeGenre={activeGenre}
              onGenreChange={setActiveGenre}
            />
          )}
        </AnimatePresence>

        <h2 className="font-heading text-2xl font-bold mb-6">
          {categories.find(c => c.id === activeCategory)?.title || "Videos"}
          {activeCategory === "10" && activeGenre !== "all" && (
            <span className="text-purple-400 ml-2">
              - {genres.find(g => g.id === activeGenre)?.title}
            </span>
          )}
        </h2>

        <VideoGrid
          videos={videos}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={!!pageToken}
          lastElementRef={lastElementRef}
        />
      </section>
    </div>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="border-t border-white/10 py-12 mt-20" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-yellow-500 flex items-center justify-center">
              <Flame className="w-5 h-5 text-black" />
            </div>
            <span className="font-heading text-lg font-bold">
              Trending<span className="text-orange-500">Z</span>
            </span>
          </div>

          {/* Instagram was listed here too, but nothing on the site sources it -
              same reason it came out of the platform filter. */}
          <div className="flex items-center gap-6 text-white/50 text-sm">
            <span className="flex items-center gap-1.5"><Youtube className="w-4 h-4" /> YouTube</span>
            <span className="flex items-center gap-1.5"><Sparkles className="w-4 h-4" /> Shorts</span>
          </div>

          {/* Hidden inside the app, where it would offer people the thing they are
              already using. IS_NATIVE is the same check the rest of the native
              wiring uses. */}
          {!IS_NATIVE && (
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 transition-colors hover:border-orange-500/40 hover:bg-white/10"
            >
              <Play className="h-5 w-5 shrink-0 text-orange-400" />
              <span className="text-left leading-tight">
                <span className="block text-[11px] uppercase tracking-wide text-white/40">Get it on</span>
                <span className="block text-sm font-semibold text-white/90">Google Play</span>
              </span>
            </a>
          )}

          {/* The policy links are not decoration: a store listing whose
              privacy policy is only reachable from the Console, and not from
              inside the product, is a routine rejection. */}
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-white/40 text-sm">
            <span>© {new Date().getFullYear()} TrendingZ</span>
            <NavLink to="/privacy" className="hover:text-orange-500 transition-colors">Privacy</NavLink>
            <NavLink to="/terms" className="hover:text-orange-500 transition-colors">Terms</NavLink>
            <NavLink to="/delete-account" className="hover:text-orange-500 transition-colors">Delete account</NavLink>
            <a href={`mailto:${LEGAL.supportEmail}`} className="hover:text-orange-500 transition-colors">Support</a>
            <NavLink to="/admin" className="hover:text-orange-500 transition-colors">Admin</NavLink>
          </div>
        </div>

        {/* Aggregators get rejected under Play's impersonation rules when it is
            not obvious they are unofficial. Saying so on every page is cheaper
            than an appeal. */}
        <p className="mt-8 text-center text-[11px] leading-relaxed text-white/25 max-w-2xl mx-auto">
          TrendingZ is an independent discovery app. It is not affiliated with, endorsed
          by or sponsored by YouTube or Google LLC. Videos play through the official
          embedded player of the source platform, and all trademarks and content belong to
          their respective owners.
        </p>
      </div>
    </footer>
  );
};

// ==================== LIVE ACTIVITY COMPONENTS ====================

// Live Activity Ticker
//
// Sits over the bottom-left of the feed, so it has to be dismissible: it used to
// have no close control and no end condition, which left it covering the first
// video card for the whole session. Dismissing it is remembered for the session
// (not forever - it is a live feed, and a fresh visit should show it again).
const TICKER_DISMISSED_KEY = "tz.ticker.dismissed";

const LiveActivityTicker = () => {
  const [activities, setActivities] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(TICKER_DISMISSED_KEY) === "1";
    } catch {
      return false;
    }
  });

  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(TICKER_DISMISSED_KEY, "1");
    } catch {
      /* private mode - dismissing for this render is still correct */
    }
  };

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        const res = await axios.get(`${API}/activity/recent?limit=5`);
        // The endpoint returns { activity, count }. Reading .length off that object
        // gave undefined, so the ticker never had anything to show and silently
        // never rendered.
        const recent = res.data?.activity || [];
        setActivities(recent);
        setCurrentIndex((prev) => (recent.length ? prev % recent.length : 0));
      } catch (e) { }
    };
    fetchActivity();
    const interval = setInterval(fetchActivity, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (activities.length > 1) {
      const rotateInterval = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % activities.length);
      }, 5000);
      return () => clearInterval(rotateInterval);
    }
  }, [activities.length]);

  if (dismissed || activities.length === 0) return null;
  const current = activities[currentIndex];

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      className="tz-safe-bottom fixed bottom-4 left-4 right-4 z-40 sm:right-auto"
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 20 }}
          className="glass rounded-xl pl-4 pr-2 py-3 flex items-center gap-3 sm:max-w-xs"
        >
          <div className="relative shrink-0">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <div className="absolute inset-0 w-2 h-2 rounded-full bg-green-500 animate-ping" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs text-white/60">User from <span className="text-orange-400 font-medium">{current?.country || 'Unknown'}</span> clicked</p>
            <p className="text-sm font-medium truncate">{current?.title || 'a video'}</p>
          </div>
          <button
            onClick={dismiss}
            aria-label="Hide live activity"
            className="shrink-0 rounded-md p-1.5 text-white/40 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
};

// One shared poll of /activity/watching for every badge on the page.
//
// Each badge used to run its own 10s interval against an endpoint that returns the
// counts for *all* videos at once, so a grid of cards meant a burst of identical
// requests. This keeps a single timer alive while at least one badge is mounted,
// and stops polling while the tab is in the background.
const watchingCounts = {
  data: {},
  subscribers: new Set(),
  timer: null,
  loaded: false,
};

const pollWatchingCounts = async () => {
  if (document.hidden) return;
  try {
    const res = await axios.get(`${API}/activity/watching`);
    watchingCounts.data = res.data.by_video || {};
  } catch (e) {
    /* leave the previous counts in place */
  } finally {
    watchingCounts.loaded = true;
    watchingCounts.subscribers.forEach((notify) => notify());
  }
};

const subscribeToWatchingCounts = (notify) => {
  watchingCounts.subscribers.add(notify);
  if (!watchingCounts.timer) {
    pollWatchingCounts();
    watchingCounts.timer = setInterval(pollWatchingCounts, 10000);
  }
  return () => {
    watchingCounts.subscribers.delete(notify);
    if (watchingCounts.subscribers.size === 0 && watchingCounts.timer) {
      clearInterval(watchingCounts.timer);
      watchingCounts.timer = null;
    }
  };
};

const useWatchingCount = (videoId) => {
  const [state, setState] = useState(() => ({
    count: watchingCounts.data[videoId] || 0,
    loading: !watchingCounts.loaded,
  }));

  useEffect(
    () =>
      subscribeToWatchingCounts(() =>
        setState({ count: watchingCounts.data[videoId] || 0, loading: false })
      ),
    [videoId]
  );

  return state;
};

// Watching Now Hover Badge
const WatchingNowHoverBadge = ({ videoId }) => {
  const { count, loading } = useWatchingCount(videoId);

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} className="absolute top-2 left-12 z-20">
      <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/70 text-white/90 backdrop-blur-sm border border-white/10">
        <span className="relative flex h-1.5 w-1.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-400"></span>
        </span>
        <span className="text-[10px] font-medium">{loading ? '...' : `${count} watching`}</span>
      </div>
    </motion.div>
  );
};

// Global Activity Map
const GlobalActivityMap = () => {
  const [dots, setDots] = useState([]);
  const [totalActive, setTotalActive] = useState(0);
  const [isDemo, setIsDemo] = useState(false);
  const [newActivityPulse, setNewActivityPulse] = useState(false);
  const prevDotsCount = useRef(0);

  useEffect(() => {
    const fetchMapData = async () => {
      try {
        const mapRes = await axios.get(`${API}/activity/live-map`);
        const newDots = mapRes.data.dots || [];
        setIsDemo(Boolean(mapRes.data.is_demo));
        if (newDots.length > prevDotsCount.current && prevDotsCount.current > 0) {
          setNewActivityPulse(true);
          setTimeout(() => setNewActivityPulse(false), 1500);
        }
        prevDotsCount.current = newDots.length;
        setDots(newDots);
        // Use watching_count from live-map response (includes demo data)
        setTotalActive(mapRes.data.watching_count || 0);
      } catch (e) { }
    };
    fetchMapData();
    const interval = setInterval(fetchMapData, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className={`relative w-full h-32 sm:h-40 bg-gradient-to-b from-orange-500/5 to-transparent rounded-xl overflow-hidden border transition-all duration-500 ${newActivityPulse ? 'border-orange-500/60 shadow-lg shadow-orange-500/20' : 'border-orange-500/20'}`}>
      <AnimatePresence>{newActivityPulse && (<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-orange-500/10 pointer-events-none z-10" />)}</AnimatePresence>
      <svg viewBox="0 0 360 180" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid meet">
        <g className="opacity-10">
          {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330, 360].map(x => <line key={`v${x}`} x1={x} y1="0" x2={x} y2="180" stroke="white" strokeWidth="0.3" />)}
          {[0, 30, 60, 90, 120, 150, 180].map(y => <line key={`h${y}`} x1="0" y1={y} x2="360" y2={y} stroke="white" strokeWidth="0.3" />)}
        </g>
        <g fill="none" stroke="rgba(255,107,0,0.3)" strokeWidth="0.8">
          <path d="M40,35 Q60,30 80,35 L100,45 Q110,55 105,70 L85,85 Q70,80 60,85 L45,75 Q35,60 40,35" />
          <path d="M80,95 Q90,90 95,100 L100,130 Q95,145 85,150 L75,140 Q70,120 80,95" />
          <path d="M170,35 Q185,30 200,35 L210,45 Q205,55 195,50 L180,55 Q170,45 170,35" />
          <path d="M175,65 Q195,60 210,70 L220,100 Q210,130 190,140 L170,130 Q165,100 175,65" />
          <path d="M210,25 Q250,20 290,30 L310,50 Q315,70 300,85 L260,90 Q230,80 220,60 L210,25" />
          <path d="M290,120 Q310,115 320,125 L325,140 Q315,150 300,145 L290,135 Q285,125 290,120" />
          <ellipse cx="258" cy="75" rx="12" ry="15" fill="rgba(255,107,0,0.15)" />
        </g>
      </svg>
      <AnimatePresence>
        {dots.map((dot, i) => (
          <motion.div key={`${dot.country}-${i}-${dot.lat.toFixed(2)}`} initial={{ scale: 0, opacity: 0 }} animate={{ scale: [0, 1.5, 1], opacity: 1 }} exit={{ scale: 0, opacity: 0 }} transition={{ duration: 0.5, delay: i * 0.03, scale: { type: "spring", stiffness: 300, damping: 15 } }} style={{ left: `${((dot.lng + 180) / 360) * 100}%`, top: `${((90 - dot.lat) / 180) * 100}%` }} className="absolute w-3 h-3 -translate-x-1.5 -translate-y-1.5">
            <span className={`absolute -inset-1 rounded-full ${dot.type === 'watching' ? 'bg-green-500/30' : 'bg-orange-500/30'} animate-ping`} style={{ animationDuration: '2s' }} />
            <span className={`absolute inset-0 rounded-full ${dot.type === 'watching' ? 'bg-green-500' : 'bg-orange-500'} animate-ping opacity-75`} />
            <span className={`absolute inset-0.5 rounded-full ${dot.type === 'watching' ? 'bg-green-400' : 'bg-orange-400'} shadow-lg`} style={{ boxShadow: dot.type === 'watching' ? '0 0 8px rgba(34,197,94,0.6)' : '0 0 8px rgba(249,115,22,0.6)' }} />
          </motion.div>
        ))}
      </AnimatePresence>
      <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between text-xs">
        <span className="flex items-center gap-1 text-white/60 bg-black/30 px-2 py-1 rounded-full"><span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />{dots.filter(d => d.type === 'click').length} clicks</span>
        <span className="flex items-center gap-1 text-white/60 bg-black/30 px-2 py-1 rounded-full"><span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />{totalActive} watching</span>
      </div>
      {/* Says DEMO when the backend is filling an empty map with simulated dots,
          so the badge never claims traffic the site does not have. */}
      <motion.div animate={newActivityPulse ? { scale: [1, 1.1, 1] } : {}} transition={{ duration: 0.3 }} className={`absolute top-2 right-2 flex items-center gap-1 text-xs text-white px-2 py-0.5 rounded-full ${isDemo ? 'bg-white/20' : 'bg-red-500/80'}`}>
        <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />{isDemo ? 'DEMO' : 'LIVE'}
      </motion.div>
    </div>
  );
};

// ==================== ADMIN PAGES ====================

// Admin Login Page (with first-time signup option)
const AdminLoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [adminExists, setAdminExists] = useState(true);
  const [isSignupMode, setIsSignupMode] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Check if any admin exists on mount
  useEffect(() => {
    const checkAdminExists = async () => {
      try {
        const res = await axios.get(`${API}/admin/check-exists`);
        setAdminExists(res.data.admin_exists);
        if (!res.data.admin_exists) {
          setIsSignupMode(true); // Auto-switch to signup if no admin
        }
      } catch (e) {
        setAdminExists(true); // Assume exists on error for security
      } finally {
        setCheckingAdmin(false);
      }
    };
    checkAdminExists();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/admin/dashboard");
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(username, password);
      toast.success("Login successful!");
      navigate("/admin/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/register`, { username, password });
      toast.success("Super Admin account created! Please login.");
      setAdminExists(true);
      setIsSignupMode(false);
      setPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  if (checkingAdmin) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md glass rounded-2xl p-8"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-orange-500 to-yellow-500 flex items-center justify-center">
            {isSignupMode ? <UserPlus className="w-8 h-8 text-black" /> : <Lock className="w-8 h-8 text-black" />}
          </div>
          <h1 className="font-heading text-2xl font-bold">
            {isSignupMode ? "Create Super Admin" : "Admin Login"}
          </h1>
          <p className="text-white/60 text-sm mt-2">
            {isSignupMode
              ? "First time setup - create your super admin account"
              : "Access the analytics dashboard"}
          </p>
        </div>

        <form onSubmit={isSignupMode ? handleSignup : handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm text-white/60 mb-2">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-orange-500 focus:outline-none text-white"
              placeholder="Enter username"
              required
            />
          </div>
          <div>
            <label className="block text-sm text-white/60 mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-orange-500 focus:outline-none text-white"
              placeholder="Enter password"
              required
            />
          </div>
          {isSignupMode && (
            <div>
              <label className="block text-sm text-white/60 mb-2">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-orange-500 focus:outline-none text-white"
                placeholder="Confirm password"
                required
              />
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-orange-500 to-yellow-500 text-black font-heading font-bold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : isSignupMode ? (
              <UserPlus className="w-5 h-5" />
            ) : (
              <Lock className="w-5 h-5" />
            )}
            {loading ? (isSignupMode ? "Creating..." : "Logging in...") : (isSignupMode ? "Create Super Admin" : "Login")}
          </button>
        </form>

        {/* Toggle between login/signup only if no admin exists */}
        {!adminExists && (
          <div className="mt-6 text-center">
            <button
              onClick={() => setIsSignupMode(!isSignupMode)}
              className="text-sm text-orange-400 hover:text-orange-300 transition-colors"
            >
              {isSignupMode ? "Already have an account? Login" : "First time? Create Super Admin"}
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

// Admin Dashboard Page
const AdminDashboardPage = () => {
  const { token, admin, logout, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState([]);
  const [topVideos, setTopVideos] = useState([]);
  const [geoData, setGeoData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("day");

  // Admin management state (super admin only)
  const [adminUsers, setAdminUsers] = useState([]);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [newAdminUsername, setNewAdminUsername] = useState("");
  const [newAdminPassword, setNewAdminPassword] = useState("");
  const [creatingAdmin, setCreatingAdmin] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/admin");
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Fetch admin users (super admin only)
  const fetchAdminUsers = useCallback(async () => {
    if (admin?.role !== "super_admin") return;
    try {
      const res = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminUsers(res.data.admins || []);
    } catch (e) {
      // Silently fail - user might not be super admin
    }
  }, [admin?.role, token]);

  useEffect(() => {
    if (token && admin?.role === "super_admin") {
      fetchAdminUsers();
    }
  }, [token, admin, fetchAdminUsers]);

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    if (!newAdminUsername || !newAdminPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    if (newAdminPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setCreatingAdmin(true);
    try {
      await axios.post(`${API}/admin/users/create`,
        { username: newAdminUsername, password: newAdminPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Admin "${newAdminUsername}" created successfully`);
      setNewAdminUsername("");
      setNewAdminPassword("");
      fetchAdminUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create admin");
    } finally {
      setCreatingAdmin(false);
    }
  };

  const handleDeleteAdmin = async (username) => {
    if (!window.confirm(`Are you sure you want to delete admin "${username}"?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${encodeURIComponent(username)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Admin "${username}" deleted`);
      fetchAdminUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete admin");
    }
  };

  const handleToggleAdminStatus = async (username) => {
    try {
      const res = await axios.put(`${API}/admin/users/${encodeURIComponent(username)}/toggle-status`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchAdminUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update admin status");
    }
  };

  useEffect(() => {
    if (!token) return;

    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [overviewRes, trendsRes, videosRes, geoRes] = await Promise.all([
          axios.get(`${API}/admin/dashboard/overview`, { headers }),
          axios.get(`${API}/admin/dashboard/trends?period=${period}`, { headers }),
          axios.get(`${API}/admin/dashboard/top-videos?limit=10&period=all`, { headers }),
          axios.get(`${API}/admin/dashboard/geo`, { headers }),
        ]);

        setOverview(overviewRes.data);
        setTrends(trendsRes.data.data || []);
        setTopVideos(videosRes.data.videos || []);
        setGeoData(geoRes.data.countries || []);
      } catch (e) {
        console.error("Failed to fetch dashboard:", e);
        if (e.response?.status === 401) {
          logout();
          navigate("/admin");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [token, period, logout, navigate]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const chartColors = ["#FF6B00", "#FFD000", "#FF4500", "#FF8C00", "#FFA500"];

  return (
    <div className="min-h-screen pt-20 px-4 pb-12" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold">Analytics Dashboard</h1>
            <p className="text-white/60">Welcome back, {admin?.username}</p>
          </div>
          <button
            onClick={() => { logout(); navigate("/"); }}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>

        {/* Overview Cards */}
        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="glass rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-orange-500" />
                </div>
                <span className="text-white/60 text-sm">Total Clicks</span>
              </div>
              <p className="font-heading text-2xl font-bold">{overview.total_clicks.toLocaleString()}</p>
            </div>

            <div className="glass rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                </div>
                <span className="text-white/60 text-sm">Today</span>
              </div>
              <p className="font-heading text-2xl font-bold">{overview.today_clicks.toLocaleString()}</p>
            </div>

            <div className="glass rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <Video className="w-5 h-5 text-blue-500" />
                </div>
                <span className="text-white/60 text-sm">Unique Videos</span>
              </div>
              <p className="font-heading text-2xl font-bold">{overview.unique_videos.toLocaleString()}</p>
            </div>

            <div className="glass rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-yellow-500" />
                </div>
                <span className="text-white/60 text-sm">Est. Revenue</span>
              </div>
              <p className="font-heading text-2xl font-bold">${overview.estimated_revenue.total}</p>
              <p className="text-xs text-white/40">@ ${overview.estimated_revenue.cpm_rate} CPM</p>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Click Trends Chart */}
          <div className="lg:col-span-2 glass rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-bold">Click Trends</h2>
              <div className="flex gap-2">
                {["hour", "day", "week"].map((p) => (
                  <button
                    key={p}
                    onClick={() => setPeriod(p)}
                    className={`px-3 py-1 rounded-lg text-sm ${period === p ? "bg-orange-500 text-black" : "bg-white/5"}`}
                  >
                    {p === "hour" ? "24h" : p === "day" ? "30d" : "12w"}
                  </button>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="date" stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 10 }} />
                <YAxis stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 10 }} />
                <Tooltip
                  contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.1)" }}
                  labelStyle={{ color: "#fff" }}
                />
                <Line type="monotone" dataKey="clicks" stroke="#FF6B00" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Content Type Distribution */}
          <div className="glass rounded-xl p-6">
            <h2 className="font-heading text-lg font-bold mb-4">Content Types</h2>
            {overview && (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={Object.entries(overview.content_types).map(([name, value]) => ({ name, value }))}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {Object.keys(overview.content_types).map((_, i) => (
                      <Cell key={i} fill={chartColors[i % chartColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Top Videos */}
          <div className="glass rounded-xl p-6">
            <h2 className="font-heading text-lg font-bold mb-4">Top Clicked Videos</h2>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {topVideos.map((video, i) => {
                // Generate YouTube thumbnail URL from video ID
                const thumbnailUrl = `https://img.youtube.com/vi/${video.video_id}/mqdefault.jpg`;
                const videoUrl = video.content_type === 'shorts'
                  ? `https://www.youtube.com/shorts/${video.video_id}`
                  : `https://www.youtube.com/watch?v=${video.video_id}`;

                // Decode URL-encoded titles
                const decodedTitle = video.title ? decodeURIComponent(video.title) : null;
                const decodedChannel = video.channel ? decodeURIComponent(video.channel) : null;

                return (
                  <a
                    key={video.video_id}
                    href={videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors group cursor-pointer"
                    data-testid={`top-video-${video.video_id}`}
                  >
                    {/* Rank Badge */}
                    <span className="w-6 h-6 rounded bg-orange-500/20 flex items-center justify-center text-xs font-bold text-orange-500 flex-shrink-0">
                      {i + 1}
                    </span>

                    {/* Thumbnail */}
                    <div className="w-20 h-12 rounded overflow-hidden flex-shrink-0 bg-black/50">
                      <img
                        src={thumbnailUrl}
                        alt={decodedTitle || 'Video thumbnail'}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>

                    {/* Video Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate group-hover:text-orange-400 transition-colors">
                        {decodedTitle || `Video ${video.video_id.substring(0, 8)}...`}
                      </p>
                      <p className="text-xs text-white/40 truncate">
                        {decodedChannel || 'Unknown Channel'}
                      </p>
                    </div>

                    {/* Click Count & Type Badge */}
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className="text-sm font-bold text-orange-500">{video.clicks}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${video.content_type === 'shorts' ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400'}`}>
                        {video.content_type === 'shorts' ? 'Short' : 'Video'}
                      </span>
                    </div>
                  </a>
                );
              })}
              {topVideos.length === 0 && (
                <p className="text-white/40 text-sm text-center py-4">No video clicks recorded yet</p>
              )}
            </div>
          </div>

          {/* Geographic Distribution */}
          <div className="glass rounded-xl p-6">
            <h2 className="font-heading text-lg font-bold mb-4">Top Countries</h2>
            {overview && (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={overview.top_countries.slice(0, 8)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis type="number" stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="country" type="category" stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 10 }} width={40} />
                  <Tooltip
                    contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.1)" }}
                  />
                  <Bar dataKey="clicks" fill="#FF6B00" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Admin Management Section - Super Admin Only */}
        {admin?.role === "super_admin" && (
          <div className="mt-8">
            <button
              onClick={() => setShowAdminPanel(!showAdminPanel)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 hover:border-purple-500/50 transition-colors mb-4"
            >
              <Shield className="w-5 h-5 text-purple-400" />
              <span className="font-heading font-bold text-purple-400">Admin Management</span>
              <ChevronDown className={`w-4 h-4 text-purple-400 transition-transform ${showAdminPanel ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {showAdminPanel && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="grid lg:grid-cols-2 gap-6">
                    {/* Create New Admin */}
                    <div className="glass rounded-xl p-6">
                      <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                        <UserPlus className="w-5 h-5 text-green-400" />
                        Create New Admin
                      </h3>
                      <form onSubmit={handleCreateAdmin} className="space-y-4">
                        <div>
                          <label className="block text-sm text-white/60 mb-2">Username</label>
                          <input
                            type="text"
                            value={newAdminUsername}
                            onChange={(e) => setNewAdminUsername(e.target.value)}
                            className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-purple-500 focus:outline-none text-white"
                            placeholder="Enter username"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-white/60 mb-2">Password</label>
                          <input
                            type="password"
                            value={newAdminPassword}
                            onChange={(e) => setNewAdminPassword(e.target.value)}
                            className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-purple-500 focus:outline-none text-white"
                            placeholder="Enter password (min 6 chars)"
                            required
                          />
                        </div>
                        <button
                          type="submit"
                          disabled={creatingAdmin}
                          className="w-full py-2 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 text-black font-heading font-bold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                          {creatingAdmin ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                          {creatingAdmin ? "Creating..." : "Create Admin"}
                        </button>
                        <p className="text-xs text-white/40 text-center">New admins will have "Admin" role (not Super Admin)</p>
                      </form>
                    </div>

                    {/* Admin Users List */}
                    <div className="glass rounded-xl p-6">
                      <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                        <Users className="w-5 h-5 text-blue-400" />
                        All Admin Users ({adminUsers.length})
                      </h3>
                      <div className="space-y-3 max-h-80 overflow-y-auto">
                        {adminUsers.map((user) => (
                          <div
                            key={user.username}
                            className={`flex items-center justify-between p-3 rounded-lg ${user.is_active ? 'bg-white/5' : 'bg-red-500/10'}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${user.role === 'super_admin' ? 'bg-purple-500/20' : 'bg-blue-500/20'}`}>
                                {user.role === 'super_admin' ? (
                                  <Shield className="w-5 h-5 text-purple-400" />
                                ) : (
                                  <Users className="w-5 h-5 text-blue-400" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium flex items-center gap-2">
                                  {user.username}
                                  {!user.is_active && <span className="text-xs px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">Disabled</span>}
                                </p>
                                <p className="text-xs text-white/40 capitalize">{user.role?.replace('_', ' ') || 'Admin'}</p>
                              </div>
                            </div>

                            {/* Actions - only for non-super-admin users and not self */}
                            {user.role !== 'super_admin' && user.username !== admin?.username && (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleToggleAdminStatus(user.username)}
                                  className={`p-2 rounded-lg transition-colors ${user.is_active ? 'bg-yellow-500/20 hover:bg-yellow-500/30' : 'bg-green-500/20 hover:bg-green-500/30'}`}
                                  title={user.is_active ? 'Disable' : 'Enable'}
                                >
                                  {user.is_active ? <UserX className="w-4 h-4 text-yellow-400" /> : <UserCheck className="w-4 h-4 text-green-400" />}
                                </button>
                                <button
                                  onClick={() => handleDeleteAdmin(user.username)}
                                  className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4 text-red-400" />
                                </button>
                              </div>
                            )}

                            {/* Badge for current user */}
                            {user.username === admin?.username && (
                              <span className="text-xs px-2 py-1 rounded-full bg-orange-500/20 text-orange-400">You</span>
                            )}
                          </div>
                        ))}

                        {adminUsers.length === 0 && (
                          <p className="text-white/40 text-sm text-center py-4">No admins found</p>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};

// Main App Component
function App() {
  return (
    <div className="App min-h-screen bg-background">
      <BrowserRouter>
        <AuthProvider>
          <UserProvider>
          <AudioProvider>
            <CountryProvider>
              <PlayerProvider>
              <Navbar />
              <Routes>
                <Route path="/" element={<HomePage />} />
                {/* Country/category landing pages: same view, addressable URL, and
                    the entries the generated sitemap advertises. */}
                <Route path="/trending/:countryCode" element={<HomePage />} />
                <Route path="/trending/:countryCode/:categoryParam" element={<HomePage />} />
                <Route path="/shorts" element={<ShortsPage />} />
                <Route path="/categories" element={<CategoriesPage />} />
                <Route path="/search" element={<SearchPage />} />
                {/* A page per video: the only URL on the site that is worth
                    sharing, indexing, or bookmarking for one piece of content. */}
                <Route path="/video/:videoId" element={<VideoPage />} />
                <Route path="/rising" element={<RisingPage />} />
                <Route path="/account" element={<AccountPage />} />
                <Route path="/library" element={<LibraryPage />} />
                <Route path="/leaderboard" element={<LeaderboardPage />} />
                <Route path="/compare" element={<ComparePage />} />
                <Route path="/archive" element={<ArchivePage />} />
                <Route path="/channel/:channelId" element={<ChannelPage />} />
                {/* Policy pages. Play requires the privacy policy and the
                    account-deletion route to be reachable in a browser by
                    someone who has never installed the app, so they are public
                    routes with no auth guard and no data fetch. The URLs
                    submitted in Play Console are built from these paths in
                    src/lib/legal.js - changing a path here means updating the
                    Console entry too. */}
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/delete-account" element={<DeleteAccountPage />} />
                <Route path="/admin" element={<AdminLoginPage />} />
                <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
              <Footer />
              <LiveActivityTicker />
              <InstallPrompt />
              <UpdatePrompt />
              {/* Sonner's default toast is a fixed 356px, which is wider than a
                  320px phone screen - the notification itself was the last thing
                  overflowing the viewport. Cap it to the screen instead. */}
              <Toaster
                position="bottom-right"
                theme="dark"
                toastOptions={{
                  style: {
                    background: '#0A0A0A',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: '#fff',
                    width: '100%',
                    maxWidth: 'calc(100vw - 2rem)',
                  },
                }}
              />
              </PlayerProvider>
            </CountryProvider>
          </AudioProvider>
          </UserProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
